import type { PageViewPayload } from '@/domain/beacon';
import type { LauncherPrompts } from '@/lib/beacon/pagePrompts';
import { fetchWithin } from '@/lib/fetchWithin';
import { logger } from '@/lib/logger';
import { asRecord, isRecord } from '@/lib/utils/isRecord';

/**
 * `fetch` has no timeout of its own, and with no retry an unbounded request leaves the
 * promise pending.
 */
const REQUEST_TIMEOUT_MS = 15_000;

export interface BrowsingGatewayConfig {
  endpoint: string;
  orgId: string;
}

export class BrowsingGateway {
  private readonly endpoint: string;
  private readonly orgId: string;

  constructor({ endpoint, orgId }: BrowsingGatewayConfig) {
    this.endpoint = endpoint;
    this.orgId = orgId;
  }

  /** No retry: a page view is answered by the next navigation or not at all. */
  async sendPageView(payload: PageViewPayload): Promise<LauncherPrompts | null> {
    const body = await this.post('page-views', payload);
    return toLauncherPrompts(body);
  }

  /**
   * Every route here is anonymous and cross-origin, so JSON goes under `text/plain` with no
   * other header: that is what makes the browser skip the preflight, and one added header
   * reinstates it with latency as the only symptom.
   */
  private async post(route: string, payload: unknown): Promise<unknown> {
    const url = `${this.endpoint}/api/v1/public/orgs/${this.orgId}/${route}`;
    // `fetchWithin` releases its own timer once the headers arrive, so the bound below is
    // what covers the read: a response that opens and then stalls its body would otherwise
    // leave the parse pending for good.
    const controller = new AbortController();
    const timer = setTimeout(
      () => controller.abort(new DOMException(`page view exceeded ${REQUEST_TIMEOUT_MS}ms`, 'TimeoutError')),
      REQUEST_TIMEOUT_MS,
    );
    try {
      const response = await fetchWithin(
        fetch,
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          credentials: 'omit',
          body: JSON.stringify(payload),
        },
        REQUEST_TIMEOUT_MS,
        controller.signal,
      );

      if (!response.ok) {
        logger.warn('[browsing] beacon refused the request', { route, status: response.status });
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.warn('[browsing] could not reach beacon', { route, error });
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}

// Only a ready entry with something to render is kept; a launcher left out falls back to its own prompts.
function toLauncherPrompts(body: unknown): LauncherPrompts | null {
  const launchers = asRecord(asRecord(body)?.launchers);
  if (!launchers) return null;
  const prompts: Record<string, string[]> = {};
  for (const [launcherId, entry] of Object.entries(launchers)) {
    if (isRecord(entry) && entry.status === 'ready' && isNonEmptyStringArray(entry.prompts)) {
      prompts[launcherId] = entry.prompts;
    }
  }
  return prompts;
}

function isNonEmptyStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every((entry) => typeof entry === 'string');
}
